/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Hook para gestión de notificaciones
 */
import { useState, useEffect, useCallback } from "react";
import { useSocket } from "../SocketContext";
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  notifyNewTraslado,
  notifyTrasladoCancelado,
} from "../services/notificationService";

const useNotifications = () => {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [isSupported] = useState(isNotificationSupported());

  const {
    socketConnected,
    onTrasladoAsignado,
    onTrasladoCancelado,
  } = useSocket();

  // Solicitar permiso
  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;

    const granted = await requestNotificationPermission();
    setPermission(granted ? 'granted' : 'denied');
    return granted;
  }, [isSupported]);

  // Configurar listeners de WebSocket para notificaciones
  useEffect(() => {
    if (!socketConnected || permission !== 'granted') return;

    // Escuchar nuevos traslados
    const unsubAsignado = onTrasladoAsignado(({ traslado }) => {
      if (traslado && document.hidden) {
        // Solo notificar si la app está en segundo plano
        notifyNewTraslado(traslado);
      }
    });

    // Escuchar traslados cancelados
    const unsubCancelado = onTrasladoCancelado(({ traslado }) => {
      if (traslado && document.hidden) {
        notifyTrasladoCancelado(traslado);
      }
    });

    return () => {
      if (typeof unsubAsignado === 'function') unsubAsignado();
      if (typeof unsubCancelado === 'function') unsubCancelado();
    };
  }, [socketConnected, permission, onTrasladoAsignado, onTrasladoCancelado]);

  return {
    isSupported,
    permission,
    requestPermission,
    isGranted: permission === 'granted',
    isDenied: permission === 'denied',
    isDefault: permission === 'default',
  };
};

export default useNotifications;
